import { api } from '@/shared/api';
import { unwrap, unwrapCollection } from '@/shared/api/envelope';

import {
    normalizePostcode,
    toProperty,
    toPropertyLookup,
    type NewProperty,
    type Property,
    type PropertyLookup,
} from './model';
import { propertyLookupEnvelopeSchema, propertyResourceSchema } from './types';

export const propertyApi = {
    /**
     * `GET /properties/lookup?postcode=&number=&suffix=` - elk adres op het
     * huisnummer uit de BAG, met oppervlakte, gebruiksdoel en bouwjaar. Niet
     * gevonden of een storing is ook een antwoord (200), geen fout.
     */
    async lookup(input: {
        postcode: string;
        houseNumber: string;
        suffix?: string | null;
    }): Promise<PropertyLookup> {
        return toPropertyLookup(
            propertyLookupEnvelopeSchema.parse(
                await api.get('/properties/lookup', {
                    query: {
                        postcode: normalizePostcode(input.postcode),
                        number: input.houseNumber.trim(),
                        suffix: input.suffix?.trim() || undefined,
                    },
                    cacheKey: false,
                }),
            ),
        );
    },

    /**
     * Het object dat dit bedrijf al kent op dit adres, of `null`. De API
     * ontdubbelt alleen op BAG-id, dus het formulier zoekt eerst zelf.
     */
    async findByAddress(input: {
        postcode: string;
        houseNumber: string;
        suffix?: string | null;
    }): Promise<Property | null> {
        const matches = unwrapCollection(
            propertyResourceSchema,
            await api.get('/properties', {
                query: { 'filter[postcode]': formatPostcode(input.postcode), per_page: 100 },
                cacheKey: false,
            }),
        ).items;
        const suffix = (input.suffix ?? '').trim().toLowerCase();
        const found = matches.find(
            (property) =>
                property.house_number.trim() === input.houseNumber.trim() &&
                (property.house_number_suffix ?? '').trim().toLowerCase() === suffix,
        );

        return found ? toProperty(found) : null;
    },

    /** `POST /properties` */
    async create(input: NewProperty): Promise<Property> {
        return toProperty(
            unwrap(
                propertyResourceSchema,
                await api.post(
                    '/properties',
                    {
                        postcode: formatPostcode(input.postcode),
                        street: input.street,
                        house_number: input.houseNumber,
                        house_number_suffix: input.houseNumberSuffix,
                        city: input.city,
                        bag_id: input.bagId ?? null,
                        bag_object_id: input.bagObjectId ?? null,
                        bag_building_ids: input.bagBuildingIds ?? [],
                        build_year: input.buildYear ?? null,
                        floor_area: input.floorArea ?? null,
                        property_type: input.propertyType ?? null,
                        usage_purposes: input.usagePurposes ?? [],
                        bag_status: input.bagStatus ?? null,
                        latitude: input.latitude ?? null,
                        longitude: input.longitude ?? null,
                    },
                    { cacheKey: false },
                ),
            ),
        );
    },
};

/** `4337kb` → `4337 KB`, zoals de API postcodes opslaat. */
function formatPostcode(postcode: string): string {
    const compact = normalizePostcode(postcode);

    return /^\d{4}[A-Z]{2}$/.test(compact) ? `${compact.slice(0, 4)} ${compact.slice(4)}` : compact;
}
